import { create } from "zustand";
import type {
  GameState,
  Player,
  TurnPhase,
  PhaseAction,
  Investment,
} from "../types/game";
import type { EarningsCard, PreparationCard, EventCard, PrepIcon } from "../types/cards";
import { earningsCards } from "../data/earningsCards";
import { preparationCards } from "../data/preparationCards";
import { eventCards } from "../data/eventCards";
import { collectibleItems } from "../data/items";
import { rollDie, resolveRoll, passiveIncomeRate } from "../engine/dice";
import { drainWLB, restoreWLB, checkBurnout, processBurnoutRecovery, WLB_START } from "../engine/wlb";
import { calcNetWorth } from "../engine/scoring";

const AVATARS = ["🦁", "🐼", "🦊", "🐸", "🦋", "🐯"];
const STARTING_CASH = 100;
const STARTING_COST_OF_LIVING = 15;

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function makePlayer(name: string, avatarKey: string, id: string): Player {
  return {
    id,
    name,
    avatarKey,
    cash: STARTING_CASH,
    prepIcons: [],
    investments: [],
    ownedItems: [],
    wlb: WLB_START,
    isBurntOut: false,
    burntOutRoundsRemaining: 0,
    lastPlayedCard: null,
  };
}

export type AppPhase = "setup" | "game";

interface GameStore extends GameState {
  appPhase: AppPhase;

  initGame: (playerNames: string[], totalRounds: number) => void;
  resetGame: () => void;

  dispatch: (action: PhaseAction) => void;
}

const emptyState: GameState = {
  phase: "DRAW_EVENT",
  round: 1,
  totalRounds: 8,
  currentPlayerIndex: 0,
  players: [],
  earningsDeck: [],
  preparationDeck: [],
  eventDeck: [],
  activeEventCard: null,
  activeDrawnCard: null,
  chosenDeck: null,
  restedThisTurn: false,
  pendingInvestment: null,
  lastDiceResult: null,
  costOfLiving: STARTING_COST_OF_LIVING,
  showCardModal: false,
  showInvestPanel: false,
  showBurnoutModal: false,
  showTurnBanner: false,
};

function applyEarningsCard(player: Player, card: EarningsCard): Player {
  const matchingIcons = card.requiredIcons.filter((icon) =>
    player.prepIcons.includes(icon)
  ).length;
  const income =
    card.requiredIcons.length > 0 && matchingIcons > 0
      ? Math.round(card.baseIncome * (1 + (card.multiplier - 1) * matchingIcons))
      : card.baseIncome;
  let updated: Player = { ...player, cash: player.cash + income, lastPlayedCard: card };
  updated = drainWLB(updated, card.wlbCost);
  updated = checkBurnout(updated);
  return updated;
}

function applyPreparationCard(player: Player, card: PreparationCard): Player {
  const newIcons: PrepIcon[] = [...player.prepIcons, ...card.grantsIcons];
  const bonus = card.oneTimeBonus ?? 0;
  let updated: Player = {
    ...player,
    prepIcons: newIcons,
    cash: player.cash + bonus,
    lastPlayedCard: card,
  };
  updated = drainWLB(updated, card.wlbCost);
  updated = checkBurnout(updated);
  return updated;
}

function applyEventCard(players: Player[], card: EventCard, currentIndex: number): Player[] {
  return players.map((player, idx) => {
    if (!card.affectsAll && idx !== currentIndex) return player;
    let cashDelta = card.cashDelta;
    if (cashDelta < 0 && card.conditionIcon && player.prepIcons.includes(card.conditionIcon)) {
      cashDelta = Math.ceil(cashDelta / 2);
    }
    const wlbDelta = card.wlbDelta;
    let updated = { ...player, cash: Math.max(0, player.cash + cashDelta) };
    if (wlbDelta > 0) updated = restoreWLB(updated, wlbDelta);
    else if (wlbDelta < 0) updated = drainWLB(updated, Math.abs(wlbDelta));
    updated = checkBurnout(updated);
    return updated;
  });
}

function applyPassiveIncome(players: Player[]): Player[] {
  return players.map((player) => {
    const income = player.investments.reduce(
      (sum, inv) => sum + inv.passiveIncomePerRound,
      0
    );
    return { ...player, cash: player.cash + income };
  });
}

function nextTurn(state: GameState): Partial<GameState> {
  const isLastPlayer = state.currentPlayerIndex === state.players.length - 1;
  let players = state.players;

  if (isLastPlayer) {
    players = applyPassiveIncome(players);
    players = players.map((p) => processBurnoutRecovery(p));
  }

  const nextPlayerIdx = isLastPlayer ? 0 : state.currentPlayerIndex + 1;
  const nextRound = isLastPlayer ? state.round + 1 : state.round;
  const nextPhase: TurnPhase = nextRound > state.totalRounds ? "GAME_OVER" : "DRAW_EVENT";
  const nextCostOfLiving = isLastPlayer
    ? STARTING_COST_OF_LIVING + Math.floor((nextRound - 1) / 2) * 5
    : state.costOfLiving;

  const nextPlayer = players[nextPlayerIdx];
  const effectivePhase: TurnPhase =
    nextPhase === "DRAW_EVENT" && nextPlayer?.isBurntOut ? "END_TURN" : nextPhase;

  return {
    players,
    currentPlayerIndex: nextPlayerIdx,
    round: nextRound,
    phase: effectivePhase,
    costOfLiving: nextCostOfLiving,
    activeEventCard: null,
    activeDrawnCard: null,
    chosenDeck: null,
    restedThisTurn: false,
    pendingInvestment: null,
    lastDiceResult: null,
    showCardModal: false,
    showInvestPanel: false,
    showBurnoutModal: false,
    showTurnBanner: nextPhase !== "GAME_OVER",
  };
}

export const useGameStore = create<GameStore>((set, get) => ({
  ...emptyState,
  appPhase: "setup",

  initGame: (playerNames, totalRounds) => {
    const players = playerNames
      .filter((n) => n.trim())
      .map((name, i) => makePlayer(name.trim(), AVATARS[i] ?? "🎮", String(i)));

    set({
      ...emptyState,
      appPhase: "game",
      players,
      totalRounds,
      earningsDeck: shuffle(earningsCards),
      preparationDeck: shuffle(preparationCards),
      eventDeck: shuffle(eventCards),
    });
  },

  resetGame: () => set({ ...emptyState, appPhase: "setup" }),

  dispatch: (action) => {
    const state = get();
    const player = state.players[state.currentPlayerIndex];

    switch (action.type) {
      case "DRAW_EVENT_CARD": {
        let deck = [...state.eventDeck];
        if (deck.length === 0) deck = shuffle(eventCards);
        const card = deck[0];
        const remaining = deck.slice(1);

        const players = applyEventCard(state.players, card, state.currentPlayerIndex);
        const updatedPlayer = players[state.currentPlayerIndex];
        const justBurnedOut = updatedPlayer.isBurntOut && !player.isBurntOut;

        set({
          players,
          eventDeck: remaining,
          activeEventCard: card,
          showBurnoutModal: justBurnedOut,
        });
        break;
      }

      case "CONFIRM_EVENT": {
        set({ phase: "CHOOSE_DECK", activeEventCard: null });
        break;
      }

      case "CHOOSE_DECK": {
        const deck = action.deck;
        let card: EarningsCard | PreparationCard;
        let newEarningsDeck = state.earningsDeck;
        let newPrepDeck = state.preparationDeck;

        if (deck === "earnings") {
          if (state.earningsDeck.length === 0) {
            newEarningsDeck = shuffle(earningsCards);
          }
          card = newEarningsDeck[0];
          newEarningsDeck = newEarningsDeck.slice(1);
        } else {
          if (state.preparationDeck.length === 0) {
            newPrepDeck = shuffle(preparationCards);
          }
          card = newPrepDeck[0];
          newPrepDeck = newPrepDeck.slice(1);
        }

        let players = [...state.players];
        if (deck === "earnings") {
          players[state.currentPlayerIndex] = applyEarningsCard(player, card as EarningsCard);
        } else {
          players[state.currentPlayerIndex] = applyPreparationCard(player, card as PreparationCard);
        }

        const updatedPlayer = players[state.currentPlayerIndex];
        const justBurnedOut = updatedPlayer.isBurntOut && !player.isBurntOut;

        set({
          players,
          earningsDeck: newEarningsDeck,
          preparationDeck: newPrepDeck,
          chosenDeck: deck,
          activeDrawnCard: card,
          phase: "REVEAL_CARD",
          showCardModal: true,
          showBurnoutModal: justBurnedOut,
        });
        break;
      }

      case "REST": {
        let updated = restoreWLB(player, 2);
        const players = [...state.players];
        players[state.currentPlayerIndex] = updated;
        set({ players, restedThisTurn: true, phase: "INVEST" });
        break;
      }

      case "CONFIRM_CARD": {
        set({ showCardModal: false, phase: "INVEST" });
        break;
      }

      case "SKIP_INVEST": {
        set({ phase: "SPEND", showInvestPanel: false });
        break;
      }

      case "COMMIT_INVEST": {
        if (action.riskLevel === "bond") {
          // Bonds: fixed price, no dice roll, guaranteed small dividend
          const investment: Investment = {
            id: `inv_${Date.now()}`,
            name: "Government Bond",
            purchasePrice: action.amount,
            currentValue: action.amount,
            riskLevel: "bond",
            passiveIncomePerRound: Math.max(1, Math.floor(action.amount * passiveIncomeRate("bond"))),
            roundPurchased: state.round,
          };
          const players = [...state.players];
          const p = players[state.currentPlayerIndex];
          players[state.currentPlayerIndex] = {
            ...p,
            cash: Math.max(0, p.cash - action.amount),
            investments: [...p.investments, investment],
          };
          set({ players, lastDiceResult: null });
        } else {
          set({
            pendingInvestment: {
              riskLevel: action.riskLevel,
              amount: action.amount,
              name: action.riskLevel === "low" ? "Index Fund" : "Tech Startup",
            },
          });
        }
        break;
      }

      case "ROLL_DICE": {
        const pending = state.pendingInvestment;
        if (!pending) break;
        const roll = rollDie();
        const result = resolveRoll(roll, pending.amount, pending.riskLevel);
        const newValue = pending.amount + result.netGain;

        const investment: Investment = {
          id: `inv_${Date.now()}`,
          name: pending.name,
          purchasePrice: pending.amount,
          currentValue: Math.max(0, newValue),
          riskLevel: pending.riskLevel,
          passiveIncomePerRound: Math.floor(pending.amount * passiveIncomeRate(pending.riskLevel)),
          roundPurchased: state.round,
        };

        const players = [...state.players];
        const p = players[state.currentPlayerIndex];
        players[state.currentPlayerIndex] = {
          ...p,
          cash: Math.max(0, p.cash - pending.amount),
          investments: [...p.investments, investment],
        };

        set({ players, lastDiceResult: result, pendingInvestment: null });
        break;
      }

      case "SELL_INVESTMENT": {
        const players = [...state.players];
        const p = players[state.currentPlayerIndex];
        const inv = p.investments.find((i) => i.id === action.investmentId);
        if (!inv) break;
        let payout: number;
        let result: import("../types/game").DiceRollResult | null = null;
        if (inv.riskLevel === "bond") {
          // Bonds always return exactly what you paid
          payout = inv.purchasePrice;
        } else {
          const roll = rollDie();
          result = resolveRoll(roll, inv.currentValue, inv.riskLevel);
          payout = Math.max(0, inv.currentValue + result.netGain);
        }
        players[state.currentPlayerIndex] = {
          ...p,
          cash: p.cash + payout,
          investments: p.investments.filter((i) => i.id !== action.investmentId),
        };
        set({ players, lastDiceResult: result });
        break;
      }

      case "CONFIRM_SPEND": {
        const players = [...state.players];
        const p = players[state.currentPlayerIndex];
        players[state.currentPlayerIndex] = {
          ...p,
          cash: Math.max(0, p.cash - state.costOfLiving),
        };
        set({ players, phase: "BUY_ITEMS" });
        break;
      }

      case "BUY_ITEM": {
        const item = collectibleItems.find((i) => i.id === action.itemId);
        if (!item) break;
        const players = [...state.players];
        const p = players[state.currentPlayerIndex];
        if (p.cash < item.cost) break;
        let updated: Player = {
          ...p,
          cash: p.cash - item.cost,
          ownedItems: [...p.ownedItems, item],
        };
        updated = restoreWLB(updated, item.wlbBonus);
        players[state.currentPlayerIndex] = updated;
        set({ players });
        break;
      }

      case "SELL_ITEM": {
        const item = player.ownedItems.find((i) => i.id === action.itemId);
        if (!item || item.resaleValue === 0) break;
        const players = [...state.players];
        let updated: Player = {
          ...player,
          cash: player.cash + item.resaleValue,
          ownedItems: player.ownedItems.filter((i) => i.id !== action.itemId),
        };
        // Selling removes its WLB bonus
        if (item.wlbBonus > 0) {
          updated = drainWLB(updated, item.wlbBonus);
        }
        players[state.currentPlayerIndex] = updated;
        set({ players });
        break;
      }

      case "SKIP_ITEMS":
      case "END_TURN": {
        set(nextTurn(state));
        break;
      }
    }
  },
}));

export const selectCurrentPlayer = (s: GameStore): Player =>
  s.players[s.currentPlayerIndex];

export const selectNetWorth = (player: Player): number => calcNetWorth(player);

export const selectAvailableItems = (s: GameStore) => {
  const player = s.players[s.currentPlayerIndex];
  if (!player) return [];
  const ownedIds = new Set(player.ownedItems.map((i) => i.id));
  return collectibleItems.filter((i) => !ownedIds.has(i.id) && player.cash >= i.cost);
};
